import type { Row, Workbook, Worksheet } from 'exceljs';
import type { ConfirmedSnapshot, PurchaseItem } from '../types';
import { formatWeekRange, weekdayLabel } from './date';
import { MEAL_LABELS } from '../types';
import { groupPurchaseItemsByCategory, purchaseDisplayCategory } from './ingredient-categories';

function styleHeader(row: Row): void {
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF255C42' } };
  row.alignment = { vertical: 'middle', horizontal: 'center' };
  row.height = 26;
}

function addTitle(sheet: Worksheet, title: string, width: number): void {
  sheet.mergeCells(1, 1, 1, width);
  const cell = sheet.getCell('A1');
  cell.value = title;
  cell.font = { size: 16, bold: true, color: { argb: 'FF173E2C' } };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F1E9' } };
  sheet.getRow(1).height = 34;
}

interface PurchaseSection {
  label: string;
  items: PurchaseItem[];
}

function purchaseSections(snapshot: ConfirmedSnapshot): PurchaseSection[] {
  const daily = snapshot.dailyPurchaseGroups ?? [];
  const extras = snapshot.extraPurchaseItems ?? [];
  if (daily.length || extras.length) {
    return [
      ...daily.map((group) => ({
        label: `${group.date} ${weekdayLabel(group.date)}`,
        items: group.items,
      })),
      ...(extras.length ? [{ label: '本周额外采购', items: extras }] : []),
    ];
  }

  // Older confirmed versions were stored as one immutable weekly summary.
  return snapshot.purchaseItems.length
    ? [{ label: '历史周汇总', items: snapshot.purchaseItems }]
    : [];
}

export async function buildPurchaseWorkbook(snapshot: ConfirmedSnapshot): Promise<Workbook> {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = '龙庭备菜助手';
  workbook.created = new Date();

  const summary = workbook.addWorksheet('采购汇总', { views: [{ state: 'frozen', ySplit: 3 }] });
  addTitle(summary, `${snapshot.kitchenName}备菜采购清单`, 5);
  summary.mergeCells('A2:E2');
  summary.getCell('A2').value = `时间：${formatWeekRange(snapshot.weekStart, snapshot.weekEnd)}`;
  summary.getCell('A2').alignment = { horizontal: 'center' };
  summary.addRow(['日期', '类别', '食材', '最终数量', '单位']);
  styleHeader(summary.getRow(3));
  purchaseSections(snapshot).forEach((section) => {
    groupPurchaseItemsByCategory(section.items).forEach((categoryGroup) => {
      categoryGroup.items.forEach((item) => {
        summary.addRow([section.label, categoryGroup.category, item.name, Number(item.finalQuantity), item.unit]);
      });
    });
  });
  summary.columns = [{ width: 24 }, { width: 14 }, { width: 28 }, { width: 16 }, { width: 14 }];

  const selections = workbook.addWorksheet('每日选择', { views: [{ state: 'frozen', ySplit: 2 }] });
  addTitle(selections, '每日菜品选择', 4);
  selections.addRow(['日期', '餐次', '菜品', '批次数量']);
  styleHeader(selections.getRow(2));
  snapshot.selections.forEach((item) =>
    selections.addRow([`${item.date} ${weekdayLabel(item.date)}`, MEAL_LABELS[item.meal], item.dishName, item.batchCount]),
  );
  (snapshot.extraSelections ?? []).forEach((item) =>
    selections.addRow(['本周', '额外采购', item.dishName, item.batchCount]),
  );
  (snapshot.extraItems ?? []).forEach((item) =>
    selections.addRow([
      '本周',
      item.sourceType === 'ingredient' ? '直接食材' : '自定义',
      item.name,
      `${item.quantity}${item.unit}`,
    ]),
  );
  selections.columns = [{ width: 24 }, { width: 12 }, { width: 26 }, { width: 14 }];

  const details = workbook.addWorksheet('计算明细', { views: [{ state: 'frozen', ySplit: 2 }] });
  addTitle(details, '采购用量计算明细', 8);
  details.addRow(['日期', '餐次', '菜品', '类别', '食材', '固定用量', '批次', '小计']);
  styleHeader(details.getRow(2));
  snapshot.selections.forEach((selection) => {
    selection.ingredients.forEach((ingredient) => {
      const subtotal = Number(ingredient.quantity) * selection.batchCount;
      details.addRow([
        selection.date,
        MEAL_LABELS[selection.meal],
        selection.dishName,
        purchaseDisplayCategory(ingredient.ingredientCategory, ingredient.name),
        ingredient.name,
        `${ingredient.quantity}${ingredient.unit}`,
        selection.batchCount,
        `${subtotal}${ingredient.unit}`,
      ]);
    });
  });
  (snapshot.dailyPurchaseGroups ?? []).forEach((group) => {
    group.items.forEach((item) => {
      item.sources?.filter((source) => source.kind === 'daily_default').forEach((source) => {
        details.addRow([
          group.date,
          '每日固定',
          '每日固定备料',
          purchaseDisplayCategory(item.ingredientCategory, item.name),
          item.name,
          `${source.contributionQuantity}${source.unit}`,
          1,
          `${source.contributionQuantity}${source.unit}`,
        ]);
      });
    });
  });
  (snapshot.extraSelections ?? []).forEach((selection) => {
    selection.ingredients.forEach((ingredient) => {
      const subtotal = Number(ingredient.quantity) * selection.batchCount;
      details.addRow([
        '本周',
        '额外采购',
        selection.dishName,
        purchaseDisplayCategory(ingredient.ingredientCategory, ingredient.name),
        ingredient.name,
        `${ingredient.quantity}${ingredient.unit}`,
        selection.batchCount,
        `${subtotal}${ingredient.unit}`,
      ]);
    });
  });
  (snapshot.extraItems ?? []).forEach((item) => {
    details.addRow([
      '本周',
      item.sourceType === 'ingredient' ? '直接食材' : '自定义',
      item.sourceType === 'ingredient' ? '直接采购食材' : '自定义采购',
      purchaseDisplayCategory(item.ingredientCategory, item.name),
      item.name,
      `${item.quantity}${item.unit}`,
      1,
      `${item.quantity}${item.unit}`,
    ]);
  });
  details.columns = [
    { width: 16 },
    { width: 10 },
    { width: 24 },
    { width: 14 },
    { width: 22 },
    { width: 16 },
    { width: 10 },
    { width: 16 },
  ];

  [summary, selections, details].forEach((sheet) => {
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber > (sheet === summary ? 3 : 2)) {
        row.height = 24;
        row.alignment = { vertical: 'middle' };
        row.eachCell((cell) => {
          cell.border = { bottom: { style: 'hair', color: { argb: 'FFD7E0D8' } } };
        });
      }
    });
  });

  return workbook;
}

export async function downloadPurchaseExcel(snapshot: ConfirmedSnapshot): Promise<void> {
  const workbook = await buildPurchaseWorkbook(snapshot);
  const bytes = await workbook.xlsx.writeBuffer();
  const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${snapshot.kitchenName}_备菜采购清单_${snapshot.weekStart}至${snapshot.weekEnd}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}
